import { useCallback, useEffect, useMemo, useState } from 'react';
import { t } from 'ttag';
import { ChatBotService } from '../services/chatBotService';
import { useTrainingStore } from '~/training/state';
import {
  ConversationSession,
  MessageData,
  emptySession,
  readSession,
  writeSession
} from './conversationSession';

export type { MessageData } from './conversationSession';

const failureMessage = (): MessageData => ({
  body: t`We couldn’t confirm a reply. Please try again later, or skip this role-play.`,
  role: 'assistant',
  isError: true
});

export const useChatConversation = ({
  chatBotService
}: {
  chatBotService: ChatBotService | null;
}) => {
  const [snapshot, setSnapshot] = useState(emptySession);
  const [loading, setLoading] = useState(false);
  const [restored, setRestored] = useState(false);
  const textLocale = useTrainingStore(s => s.textLocale);
  const session = useMemo(() => {
    const state = {
      snapshot: emptySession(),
      key: null as string | null,
      pending: false,
      generation: 0
    };
    const ready = (async () => {
      state.key = (await chatBotService?.sessionStorageKey()) || null;
      state.snapshot = readSession(state.key);
    })();
    return { state, ready };
  }, [chatBotService]);

  useEffect(() => {
    const generation = session.state.generation;
    setSnapshot(emptySession());
    setRestored(false);
    setLoading(false);
    session.ready.then(() => {
      if (generation !== session.state.generation) return;
      setSnapshot(session.state.snapshot);
      setRestored(true);
    });
    return () => {
      session.state.generation += 1;
    };
  }, [session]);

  const commit = useCallback(
    (update: Partial<ConversationSession>) => {
      session.state.snapshot = { ...session.state.snapshot, ...update };
      writeSession(session.state.key, session.state.snapshot);
      setSnapshot(session.state.snapshot);
    },
    [session]
  );

  const sendMessage = useCallback(
    async (text: string, subject?: string, showUserMessage = true) => {
      const { state } = session;
      if (!chatBotService || !text.trim() || state.pending) return;
      const generation = state.generation;
      const current = () => generation === state.generation;
      state.pending = true;
      setLoading(true);
      try {
        await session.ready;
        if (!current() || (!showUserMessage && state.snapshot.initialSent))
          return;
        commit({
          pending: true,
          initialFailed: false,
          ...(showUserMessage
            ? {
                draft: text,
                messages: [
                  ...state.snapshot.messages,
                  { body: text, role: 'user' }
                ]
              }
            : {})
        });
        if (!state.snapshot.conversationId) {
          const response = await chatBotService.createChatbotConversation();
          if (!current()) return;
          if (
            response.error ||
            typeof response.conversation_id !== 'string' ||
            !response.conversation_id.trim()
          )
            throw new Error('Conversation could not be started');
          // Commit the confirmed ID before continuing; React may not have rendered.
          commit({ conversationId: response.conversation_id });
        }
        // REST acknowledges the reply itself, rather than only a socket send.
        const response = await chatBotService.sendChatbotMessage(
          state.snapshot.conversationId!,
          text,
          subject
        );
        if (!current()) return;
        if (
          response.error ||
          typeof response.text !== 'string' ||
          !response.text.trim()
        )
          throw new Error('Reply could not be confirmed');
        commit({
          messages: [
            ...state.snapshot.messages,
            { body: response.text, role: 'assistant' }
          ],
          initialSent: true,
          ...(showUserMessage ? { draft: '' } : {})
        });
      } catch (error) {
        if (!current()) return;
        commit({
          messages: [...state.snapshot.messages, failureMessage()],
          initialFailed: !showUserMessage
        });
        throw error;
      } finally {
        if (current()) {
          state.pending = false;
          commit({ pending: false });
          setLoading(false);
        }
      }
    },
    [chatBotService, session, commit]
  );

  const sendInitialMessage = useCallback(
    async (subject?: string) => {
      const greeting =
        textLocale && textLocale.toLowerCase() !== 'en'
          ? `hi <userLangSelected>${textLocale}</userLangSelected>`
          : 'Hi';
      try {
        await sendMessage(greeting, subject, false);
      } catch {
        /* The failure is already visible. */
      }
    },
    [sendMessage, textLocale]
  );

  const resetConversation = useCallback(async () => {
    const { state } = session;
    if (!chatBotService || state.pending) return;
    const generation = state.generation;
    state.pending = true;
    setLoading(true);
    try {
      await session.ready;
      if (generation !== state.generation) return;
      const response = await chatBotService.resetChatbotConversation();
      if (generation !== state.generation) return;
      if (
        response.error ||
        typeof response.conversation_id !== 'string' ||
        !response.conversation_id.trim()
      )
        throw new Error('Conversation could not be reset');
      commit({ ...emptySession(), conversationId: response.conversation_id });
    } catch {
      if (generation === state.generation)
        commit({ messages: [...state.snapshot.messages, failureMessage()] });
    } finally {
      if (generation === state.generation) {
        state.pending = false;
        setLoading(false);
      }
    }
  }, [chatBotService, session, commit]);

  const setDraft = useCallback((draft: string) => commit({ draft }), [commit]);
  return {
    ...snapshot,
    loading,
    restored,
    setDraft,
    sendMessage,
    sendInitialMessage,
    resetConversation
  };
};
